import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, ActivatedRoute } from '@angular/router';
import { HttpParams } from '@angular/common/http';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatListModule } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { EMPTY, Observable, forkJoin } from 'rxjs';
import { expand, scan, last } from 'rxjs/operators';
import { CarService } from '../../../core/api/car.service';
import { BookingService } from '../../../core/api/booking.service';
import { RentalService } from '../../../core/api/rental.service';
import { Car, CarAvailability } from '../../../core/models/car.model';
import { BookingList, BookingStatus } from '../../../core/models/booking.model';
import { RentalList, RentalStatus } from '../../../core/models/rental.model';
import { PaginatedResponse } from '../../../core/models/api.model';
import { parseApiDateTime } from '../../../shared/utils/date.utils';
import { StatusLabelPipe } from '../../../shared/pipes/status-label.pipe';
import { BookingCardComponent } from '../../bookings/booking-card/booking-card.component';
import { RentalCardComponent } from '../../rentals/rental-card/rental-card.component';

type TimelineEntry =
  | { kind: 'booking'; date: Date | null; booking: BookingList }
  | { kind: 'rental'; date: Date | null; rental: RentalList };

@Component({
  selector: 'app-car-detail',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatChipsModule,
    MatExpansionModule,
    MatListModule,
    MatProgressSpinnerModule,
    StatusLabelPipe,
    BookingCardComponent,
    RentalCardComponent,
  ],
  templateUrl: './car-detail.component.html',
  styleUrl: './car-detail.component.scss',
})
export class CarDetailComponent implements OnInit {
  loading = true;
  car: Car | null = null;
  availability: CarAvailability | null = null;

  timeline: TimelineEntry[] = [];
  timelineLoading = false;
  timelineError = false;

  // Активные записи (текущие и будущие) — показываются в основном списке.
  get activeTimeline(): TimelineEntry[] {
    return this.timeline.filter((entry) => !this.isFinished(entry));
  }

  // Завершённые записи — скрыты в аккордеоне в конце списка.
  get finishedTimeline(): TimelineEntry[] {
    return this.timeline.filter((entry) => this.isFinished(entry));
  }

  constructor(
    private route: ActivatedRoute,
    private carService: CarService,
    private bookingService: BookingService,
    private rentalService: RentalService,
  ) {}

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (!id) return;

    this.carService.get(id).subscribe(car => {
      this.car = car;
    });

    const today = new Date().toISOString().split('T')[0];
    this.carService.getCarAvailability(id, today).subscribe({
      next: (avail) => {
        this.availability = avail;
        this.loading = false;
      },
      error: () => { this.loading = false; },
    });

    this.loadTimeline(id);
  }

  private loadTimeline(carId: number): void {
    this.timelineLoading = true;
    this.timelineError = false;

    const bookingParams = new HttpParams().set('car', String(carId)).set('ordering', 'start_date');
    const rentalParams = new HttpParams().set('car', String(carId)).set('ordering', 'handover_datetime');

    forkJoin({
      bookings: this.fetchAll<BookingList>((params) => this.bookingService.list(params), bookingParams),
      rentals: this.fetchAll<RentalList>((params) => this.rentalService.list(params), rentalParams),
    }).subscribe({
      next: ({ bookings, rentals }) => {
        const entries: TimelineEntry[] = [
          ...bookings.map((booking): TimelineEntry => ({
            kind: 'booking',
            date: parseApiDateTime(booking.start_date),
            booking,
          })),
          ...rentals.map((rental): TimelineEntry => ({
            kind: 'rental',
            date: parseApiDateTime(rental.handover_datetime),
            rental,
          })),
        ];
        entries.sort((a, b) => this.compareTimelineDates(a.date, b.date));
        this.timeline = entries;
        this.timelineLoading = false;
      },
      error: () => {
        this.timeline = [];
        this.timelineLoading = false;
        this.timelineError = true;
      },
    });
  }

  private fetchAll<T>(
    req: (params: HttpParams) => Observable<PaginatedResponse<T>>,
    baseParams: HttpParams,
  ): Observable<T[]> {
    return req(baseParams).pipe(
      expand((res) => (res.next ? req(baseParams.set('page', res.next)) : EMPTY)),
      scan((acc: T[], res) => acc.concat(res.results), [] as T[]),
      last(),
    );
  }

  // Завершённой считается бронь в статусе completed/cancelled
  // и прокат в статусе returned.
  private isFinished(entry: TimelineEntry): boolean {
    if (entry.kind === 'booking') {
      return entry.booking.status === BookingStatus.COMPLETED
        || entry.booking.status === BookingStatus.CANCELLED;
    }
    return entry.rental.status === RentalStatus.RETURNED;
  }

  // Хронологическая сортировка по убыванию (сначала новые);
  // null-даты трактуем как наименее релевантные — уходят в конец списка.
  private compareTimelineDates(a: Date | null, b: Date | null): number {
    if (a === null && b === null) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    return b.getTime() - a.getTime();
  }
}